import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { getUser } from '../auth';
import { verifierEtAttribuerBadges } from '../badges';

const types = [
  { label: 'Nature', emoji: '🌲' },
  { label: 'Sport', emoji: '⚽' },
  { label: 'Créatif', emoji: '🎨' },
  { label: 'Jeux', emoji: '🏃' },
  { label: 'Cuisine', emoji: '🍳' },
  { label: 'Culture', emoji: '🎭' },
];

export default function CreerActivite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = getUser();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    type: null, titre: '', lieu: '', date: '', heure: '',
    ageMin: '', ageMax: '', maxFamilles: '5', maxEnfants: '10', description: '',
  });
  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [lieuValide, setLieuValide] = useState(false);
  const [lieuCoords, setLieuCoords] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [charteAcceptee, setCharteAcceptee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estReorganisation, setEstReorganisation] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }

    // Pré-remplir depuis les paramètres URL (réorganisation)
    const titre = searchParams.get('titre');
    const typeParam = searchParams.get('type');
    const emoji = searchParams.get('emoji');
    const lieu = searchParams.get('lieu');
    const description = searchParams.get('description');
    const ageMin = searchParams.get('ageMin');
    const ageMax = searchParams.get('ageMax');
    const maxFamilles = searchParams.get('maxFamilles');

    if (titre) {
      setEstReorganisation(true);
      const typeObj = types.find(t => t.label === typeParam) ||
        (emoji ? { label: typeParam || 'Nature', emoji } : types[0]);

      setForm(prev => ({
        ...prev,
        titre: titre || '',
        lieu: lieu || '',
        description: description || '',
        ageMin: ageMin || '',
        ageMax: ageMax || '',
        maxFamilles: maxFamilles || '5',
        type: typeObj,
      }));

      if (lieu) setLieuValide(true);
      if (typeObj) setStep(2); // Sauter l'étape 1 si type déjà connu
    }
  }, []);

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleLieuChange = async (val) => {
    update('lieu', val);
    setLieuValide(false);
    setLieuCoords(null);
    if (val.length < 3) { setSuggestions([]); return; }
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(val)}&limit=5`);
      const data = await res.json();
      setSuggestions(data.features || []);
    } catch (e) { setSuggestions([]); }
  };

  const selectLieu = (s) => {
    update('lieu', s.properties.label);
    setLieuCoords({ lat: s.geometry.coordinates[1], lon: s.geometry.coordinates[0] });
    setLieuValide(true);
    setSuggestions([]);
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhoto(URL.createObjectURL(file));
  };

  const validerEtape2 = () => {
    const errs = {};
    if (!form.titre.trim()) errs.titre = 'Donne un titre à ton activité';
    if (!form.lieu.trim()) errs.lieu = 'Entre le lieu de l\'activité';
    if (!form.date) errs.date = 'Choisis une date';
    if (!form.heure) errs.heure = 'Choisis une heure';
    if (!form.ageMin) errs.ageMin = 'Âge min requis';
    if (!form.ageMax) errs.ageMax = 'Âge max requis';
    if (parseInt(form.ageMin) > parseInt(form.ageMax)) errs.age = 'L\'âge min doit être inférieur à l\'âge max';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    try {
      const ext = photoFile.name.split('.').pop();
      const path = `activites/${Date.now()}.${ext}`;
      await supabase.storage.from('photos').upload(path, photoFile, { upsert: true });
      const { data } = supabase.storage.from('photos').getPublicUrl(path);
      return data.publicUrl;
    } catch (err) { return null; }
  };

  const publier = async () => {
    if (!charteAcceptee) { alert('Tu dois accepter la charte Wooki pour publier.'); return; }
    setLoading(true);
    try {
      const photoUrl = await uploadPhoto();
      const { error } = await supabase.from('activites').insert({
        titre: form.titre.trim(),
        type: form.type.label,
        emoji: form.type.emoji,
        lieu: form.lieu,
        date: form.date,
        heure: form.heure,
        age_min: parseInt(form.ageMin) || 0,
        age_max: parseInt(form.ageMax) || 18,
        max_familles: parseInt(form.maxFamilles),
        max_enfants: parseInt(form.maxEnfants),
        description: form.description.trim(),
        latitude: lieuCoords?.lat || null,
        longitude: lieuCoords?.lon || null,
        photo_url: photoUrl,
        user_id: user?.id || null,
      });
      if (error) throw error;
      if (user?.id) await verifierEtAttribuerBadges(user.id);
      navigate('/feed');
    } catch (err) { alert('Erreur lors de la création. Réessaie !'); }
    setLoading(false);
  };

  const S = {
    input: { width: '100%', padding: '14px 16px', border: '2px solid #E8EFE9', borderRadius: 16, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#1C2B1E', background: 'white', outline: 'none', boxSizing: 'border-box', marginBottom: 4 },
    inputError: { width: '100%', padding: '14px 16px', border: '2px solid #FF7043', borderRadius: 16, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#1C2B1E', background: 'white', outline: 'none', boxSizing: 'border-box', marginBottom: 4 },
    label: { fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 },
    error: { fontSize: '0.75rem', color: '#FF7043', marginBottom: 10, marginLeft: 4 },
    btn: { width: '100%', padding: '17px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 6px 20px rgba(61,154,106,0.35)', marginTop: 8 },
  };

  const totalSteps = estReorganisation ? 2 : 3;

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7' }}>

      {/* HEADER */}
      <div style={{ background: 'white', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 2px 12px rgba(28,43,30,0.06)', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => step > (estReorganisation ? 2 : 1) ? setStep(step - 1) : navigate('/feed')} style={{ width: 38, height: 38, borderRadius: 12, background: '#F3F4F2', border: 'none', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E' }}>
            {estReorganisation ? '🔄 Réorganiser la sortie' : step === 1 ? 'Type de sortie' : step === 2 ? 'Les détails' : 'Finaliser'}
          </div>
          {estReorganisation && <div style={{ fontSize: '0.7rem', color: '#3D9A6A', fontWeight: 700 }}>Infos pré-remplies 🌿</div>}
        </div>
        <div onClick={() => navigate('/feed')} style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', cursor: 'pointer' }}>
          <span style={{ color: '#3D9A6A' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
        </div>
      </div>

      {/* BARRE PROGRESSION */}
      <div style={{ height: 4, background: '#E8EFE9' }}>
        <div style={{ height: '100%', background: 'linear-gradient(90deg, #52C289, #3D9A6A)', borderRadius: 100, transition: 'width 0.5s', width: estReorganisation ? (step === 2 ? '50%' : '100%') : `${(step / 3) * 100}%` }} />
      </div>

      {/* BANNIÈRE RÉORGANISATION */}
      {estReorganisation && (
        <div style={{ background: 'linear-gradient(135deg, #EDF7F1, #D4EEE0)', padding: '12px 20px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '1.2rem' }}>🔄</span>
          <div style={{ fontSize: '0.82rem', color: '#4A6355', fontWeight: 600 }}>
            Les infos de ta sortie précédente sont pré-remplies. Choisis juste une nouvelle date !
          </div>
        </div>
      )}

      <div style={{ padding: '24px 20px 80px' }}>

        {/* ÉTAPE 1 — TYPE (seulement si pas réorganisation) */}
        {step === 1 && !estReorganisation && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ fontSize: '3rem', marginBottom: 10 }}>🎯</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.5rem', color: '#1C2B1E', marginBottom: 6 }}>Quel type de sortie ?</div>
              <div style={{ fontSize: '0.85rem', color: '#8A9E8D' }}>Choisis une catégorie pour ta sortie</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
              {types.map((t, i) => (
                <div key={i} onClick={() => update('type', t)} style={{
                  background: form.type?.label === t.label ? 'linear-gradient(135deg, #EDF7F1, #D4EEE0)' : 'white',
                  border: form.type?.label === t.label ? '2.5px solid #3D9A6A' : '2.5px solid #E8EFE9',
                  borderRadius: 20, padding: '20px 16px', textAlign: 'center', cursor: 'pointer',
                  boxShadow: form.type?.label === t.label ? '0 4px 16px rgba(61,154,106,0.15)' : '0 2px 8px rgba(28,43,30,0.05)',
                  transition: 'all 0.2s',
                }}>
                  <div style={{ fontSize: '2.2rem', marginBottom: 8 }}>{t.emoji}</div>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#1C2B1E' }}>{t.label}</div>
                </div>
              ))}
            </div>
            <button style={{ ...S.btn, background: form.type ? '#3D9A6A' : '#E8EFE9', color: form.type ? 'white' : '#8A9E8D', boxShadow: form.type ? '0 6px 20px rgba(61,154,106,0.35)' : 'none' }}
              onClick={() => form.type && setStep(2)}>
              Continuer →
            </button>
          </div>
        )}

        {/* ÉTAPE 2 — DÉTAILS */}
        {step === 2 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>{form.type?.emoji}</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.4rem', color: '#1C2B1E' }}>
                {estReorganisation ? 'Nouvelle date 📅' : 'Les détails'}
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
              <label style={S.label}>Titre de l'activité ✏️</label>
              <input style={errors.titre ? S.inputError : S.input} placeholder="Ex: Balade en forêt de Chambaran" value={form.titre}
                onChange={e => { update('titre', e.target.value); setErrors({...errors, titre: null}); }} />
              {errors.titre && <div style={S.error}>⚠️ {errors.titre}</div>}

              <label style={{ ...S.label, marginTop: 12 }}>Point de ralliement 📍</label>
              <div style={{ position: 'relative' }}>
                <input style={{ ...(errors.lieu ? S.inputError : S.input), borderColor: lieuValide ? '#3D9A6A' : errors.lieu ? '#FF7043' : '#E8EFE9', paddingRight: lieuValide ? 44 : 16 }}
                  placeholder="Ex: Parking forêt, Mours-Saint-Eusèbe"
                  value={form.lieu} onChange={e => handleLieuChange(e.target.value)} />
                {lieuValide && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#3D9A6A', fontSize: '1.2rem' }}>✅</div>}
                {suggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(28,43,30,0.12)', zIndex: 100, overflow: 'hidden', marginTop: 4 }}>
                    {suggestions.map((s, i) => (
                      <div key={i} onClick={() => selectLieu(s)} style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '0.85rem', color: '#1C2B1E', borderBottom: i < suggestions.length - 1 ? '1px solid #F3F4F2' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span>📍</span>
                        <div>
                          <div style={{ fontWeight: 700 }}>{s.properties.label}</div>
                          {s.properties.context && <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{s.properties.context}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {errors.lieu && <div style={S.error}>⚠️ {errors.lieu}</div>}
            </div>

            <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Date 📅</label>
                  <input style={errors.date ? S.inputError : S.input} type="date" value={form.date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={e => { update('date', e.target.value); setErrors({...errors, date: null}); }} />
                  {errors.date && <div style={S.error}>⚠️ {errors.date}</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Heure ⏱</label>
                  <input style={errors.heure ? S.inputError : S.input} type="time" value={form.heure}
                    onChange={e => { update('heure', e.target.value); setErrors({...errors, heure: null}); }} />
                  {errors.heure && <div style={S.error}>⚠️ {errors.heure}</div>}
                </div>
              </div>

              <label style={{ ...S.label, marginTop: 8 }}>Âge des enfants 👶</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input style={{ ...S.input, flex: 1 }} placeholder="Min" type="number" min="0" max="17" value={form.ageMin}
                  onChange={e => { update('ageMin', e.target.value); setErrors({...errors, ageMin: null, age: null}); }} />
                <div style={{ color: '#8A9E8D', fontWeight: 700 }}>→</div>
                <input style={{ ...S.input, flex: 1 }} placeholder="Max" type="number" min="1" max="18" value={form.ageMax}
                  onChange={e => { update('ageMax', e.target.value); setErrors({...errors, ageMax: null, age: null}); }} />
              </div>
              {errors.age && <div style={S.error}>⚠️ {errors.age}</div>}
            </div>

            {!estReorganisation && (
              <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
                <label style={S.label}>Photo du lieu 📸 (optionnel)</label>
                {photo ? (
                  <div style={{ position: 'relative' }}>
                    <img src={photo} alt="lieu" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 16 }} />
                    <button onClick={() => { setPhoto(null); setPhotoFile(null); }} style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
                  </div>
                ) : (
                  <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 110, border: '2.5px dashed #C5DFC9', borderRadius: 16, background: '#FAFAF7', cursor: 'pointer', gap: 6 }}>
                    <span style={{ fontSize: '2rem' }}>📸</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3D9A6A' }}>Ajouter une photo</span>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
                  </label>
                )}
              </div>
            )}

            <button style={S.btn} onClick={() => { if (validerEtape2()) setStep(3); }}>
              Continuer →
            </button>
          </div>
        )}

        {/* ÉTAPE 3 — FINALISER */}
        {step === 3 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>👥</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.4rem', color: '#1C2B1E', marginBottom: 6 }}>Derniers détails</div>
            </div>

            <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
              <label style={S.label}>Familles maximum 👨‍👩‍👧</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['3', '4', '5', '6', '8', '10'].map(n => (
                  <div key={n} onClick={() => update('maxFamilles', n)} style={{ flex: 1, padding: '12px 0', textAlign: 'center', background: form.maxFamilles === n ? '#3D9A6A' : '#F3F4F2', color: form.maxFamilles === n ? 'white' : '#1C2B1E', borderRadius: 12, fontWeight: 800, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s' }}>{n}</div>
                ))}
              </div>

              <label style={S.label}>Enfants maximum 🧒</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['5', '8', '10', '12', '15', '20'].map(n => (
                  <div key={n} onClick={() => update('maxEnfants', n)} style={{ flex: 1, padding: '12px 0', textAlign: 'center', background: form.maxEnfants === n ? '#FF7043' : '#F3F4F2', color: form.maxEnfants === n ? 'white' : '#1C2B1E', borderRadius: 12, fontWeight: 800, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s' }}>{n}</div>
                ))}
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
              <label style={S.label}>Description 💬</label>
              <textarea style={{ ...S.input, height: 110, resize: 'none' }}
                placeholder="Conseils, matériel à apporter..."
                value={form.description} onChange={e => update('description', e.target.value)} />
            </div>

            {/* RÉCAP */}
            <div style={{ background: 'linear-gradient(135deg, #EDF7F1, #D4EEE0)', borderRadius: 24, padding: '20px', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#3D9A6A', marginBottom: 14 }}>✅ Récapitulatif</div>
              {[
                { icon: form.type?.emoji, label: form.titre },
                { icon: '📅', label: new Date(form.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) + ' à ' + form.heure },
                { icon: '📍', label: form.lieu.split(',')[0] },
                { icon: '👶', label: (form.ageMin || '0') + ' – ' + (form.ageMax || '18') + ' ans' },
                { icon: '👨‍👩‍👧', label: 'Max ' + form.maxFamilles + ' familles · ' + form.maxEnfants + ' enfants' },
              ].map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: '1.1rem', width: 28 }}>{r.icon}</span>
                  <span style={{ fontSize: '0.85rem', color: '#4A6355', fontWeight: 600 }}>{r.label}</span>
                </div>
              ))}
            </div>

            {/* CHARTE */}
            <div onClick={() => setCharteAcceptee(!charteAcceptee)}
              style={{ background: charteAcceptee ? '#EDF7F1' : 'white', border: charteAcceptee ? '2px solid #3D9A6A' : '2px solid #E8EFE9', borderRadius: 20, padding: '16px', marginBottom: 20, cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'flex-start', transition: 'all 0.2s' }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: charteAcceptee ? '#3D9A6A' : '#F3F4F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontWeight: 800 }}>
                {charteAcceptee ? '✓' : ''}
              </div>
              <div>
                <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#1C2B1E', marginBottom: 4 }}>🔒 Je respecte la charte Wooki</div>
                <div style={{ fontSize: '0.75rem', color: '#8A9E8D', lineHeight: 1.5 }}>
                  Je m'engage à être présent(e) avec les enfants, bienveillant(e), et à ne pas échanger de coordonnées personnelles.
                </div>
              </div>
            </div>

            <button style={{ ...S.btn, opacity: loading || !charteAcceptee ? 0.6 : 1, background: charteAcceptee ? '#3D9A6A' : '#E8EFE9', color: charteAcceptee ? 'white' : '#8A9E8D', boxShadow: charteAcceptee ? '0 6px 20px rgba(61,154,106,0.35)' : 'none' }}
              onClick={publier} disabled={loading || !charteAcceptee}>
              {loading ? '⏳ Publication en cours...' : charteAcceptee ? (estReorganisation ? '🔄 Republier la sortie !' : '🎉 Publier l\'activité !') : '☝️ Accepte la charte pour publier'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}